// CreateItemMaster.jsx
// Item Creation screen: a form to create or edit an item next to a searchable, sortable, paged
// list of existing items. Click a row (or its pencil) to edit it; the form shows which item is
// being edited and can be cancelled.
import React, { useEffect, useRef, useState } from "react";
import UnitSelect from "./UnitSelect";
import useAppSettings, { hasDecimals } from "./useAppSettings";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit, Inventory2Outlined, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  itemName: "",
  unitName: "",
  standardPrice: "",
  barcode: "",
  itemCode: "",
  taxRate: "",
  hsnCode: "",
  unitId: "",
};

const blank = (v) => (v === null || v === undefined ? "" : v);

const formatPrice = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2));

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ display: "block", fontWeight: 600, letterSpacing: "0.08em", mb: 1.5 }}
  >
    {children}
  </Typography>
);

const CreateItemMaster = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("itemName");

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [toast, setToast] = useState(null); // { severity, message }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null); // the item as loaded from the list
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formKey, setFormKey] = useState(0); // remounts the unit picker so a reset also clears its typed text
  const nameRef = useRef(null);

  const editingItemId = editingItem?.id ?? null;
  const { settings } = useAppSettings();
  const priceDecimals = settings.itemMasterPriceDecimals;
  // With decimals off, an item already priced 42.50 can still be saved as long as its price is unchanged.
  const keepsOldDecimalPrice = editingItem && Number(formData.standardPrice) === Number(editingItem.standardPrice);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, rowsPerPage, sortOrder, sortField]);

  const fetchItems = async () => {
    const tenancyId = localStorage.getItem("tenancyId");
    const token = localStorage.getItem("jwtToken");

    setLoading(true);
    try {
      const response = await fetch(
        `/api/${tenancyId}/items-search?query=${encodeURIComponent(searchQuery)}&page=${page}&size=${rowsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        console.error("Error: ", response.statusText);
        setItems([]);
        setTotalItems(0);
        return;
      }
      const data = await response.json();
      setItems(data.content || []);
      setTotalItems(data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
    setPage(0);
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setFieldErrors({});
    setErrorMessage(null);
    setEditingItem(null);
    setFormKey((k) => k + 1);
    setTimeout(() => nameRef.current?.focus(), 0);
  };

  const handleEdit = (item) => {
    setFormData({
      itemName: blank(item.itemName),
      unitName: blank(item.unitName),
      standardPrice: blank(item.standardPrice),
      barcode: blank(item.barcode),
      itemCode: blank(item.itemCode),
      taxRate: blank(item.taxRate),
      hsnCode: blank(item.hsnCode),
      unitId: blank(item.unitId),
    });
    setEditingItem(item);
    setFieldErrors({});
    setErrorMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => nameRef.current?.focus(), 0);
  };

  const setField = (name, value) => {
    setFormData((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((errs) => ({ ...errs, [name]: undefined }));
  };

  const handleChange = (e) => setField(e.target.name, e.target.value);

  const validateForm = () => {
    const required = t("requiredField", "Required");
    const errs = {};
    if (!String(formData.itemName).trim()) errs.itemName = required;
    if (!String(formData.unitName).trim()) errs.unitName = required;
    if (!String(formData.barcode).trim()) errs.barcode = required;
    if (!String(formData.hsnCode).trim()) errs.hsnCode = required;

    if (formData.standardPrice === "") errs.standardPrice = required;
    else if (Number(formData.standardPrice) <= 0) errs.standardPrice = t("positivePrice", "Price must be greater than zero.");
    else if (!priceDecimals && hasDecimals(formData.standardPrice) && !keepsOldDecimalPrice)
      errs.standardPrice = "Decimal prices are turned off in Settings. Enter a whole number.";

    if (formData.taxRate === "") errs.taxRate = required;
    else if (Number(formData.taxRate) < 0) errs.taxRate = t("positiveTaxRate", "Tax rate cannot be negative.");

    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setErrorMessage(t("requiredFields", "Please fill in the highlighted fields."));
      const first = ["itemName", "unitName", "itemCode", "barcode", "standardPrice", "taxRate", "hsnCode"].find(
        (k) => errs[k]
      );
      document.querySelector(`[data-field="${first}"] input`)?.focus();
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const tenancyId = localStorage.getItem("tenancyId");
    const token = localStorage.getItem("jwtToken");
    const isEdit = Boolean(editingItemId);

    setSaving(true);
    try {
      const response = await fetch(isEdit ? `/api/${tenancyId}/items/${editingItemId}` : `/api/${tenancyId}/items`, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setToast({
          severity: "success",
          message: `${isEdit ? t("updateItemSuccess", "Item updated") : t("createItemSuccess", "Item created")} (${formData.itemName})`,
        });
        resetForm();
        fetchItems();
      } else {
        let msg = isEdit ? t("updateFailed", "Could not update the item.") : t("createFailed", "Could not create the item.");
        try {
          const errorData = await response.json();
          msg = errorData.errorMessage || msg;
        } catch {
          // keep the generic message
        }
        setErrorMessage(msg);
      }
    } catch (error) {
      console.error(isEdit ? "Error updating item:" : "Error creating new Item:", error);
      setErrorMessage(isEdit ? t("updateFailed", "Could not update the item.") : t("createFailed", "Could not create the item."));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const item = deleteTarget;
    const tenancyId = localStorage.getItem("tenancyId");
    const token = localStorage.getItem("jwtToken");

    setDeleting(true);
    try {
      const response = await fetch(`/api/${tenancyId}/items/${item.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setToast({ severity: "success", message: `Item deleted (${item.itemName})` });
        if (editingItemId === item.id) resetForm();
        fetchItems();
      } else {
        setToast({ severity: "error", message: "Failed to delete item." });
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      setToast({ severity: "error", message: "An error occurred while deleting the item." });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const fieldProps = (name) => ({
    name,
    value: formData[name],
    onChange: handleChange,
    error: Boolean(fieldErrors[name]),
    fullWidth: true,
    "data-field": name,
  });

  const priceHelp = priceDecimals ? "" : "Whole numbers only (decimals are off in Settings).";

  const sortable = (field, label, align) => (
    <TableCell align={align} sortDirection={sortField === field ? sortOrder : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortOrder : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const hideOnPhone = { display: { xs: "none", sm: "table-cell" } };

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Inventory2Outlined color="primary" sx={{ fontSize: 32 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {t("createItem")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add new items or pick one from the list to edit it.
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={3} alignItems="flex-start">
        {/* Item form */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, position: { md: "sticky" }, top: { md: 80 } }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                {editingItem ? t("editItem") : "New item"}
              </Typography>
              {editingItem && (
                <Tooltip title="Stop editing and start a new item">
                  <IconButton size="small" onClick={resetForm} aria-label="Cancel edit">
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {editingItem && (
              <Alert severity="info" icon={<Edit fontSize="inherit" />} sx={{ mb: 2 }}>
                Editing <strong>{editingItem.itemName}</strong>. Save to update it, or cancel to add a new item.
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                {errorMessage}
              </Alert>
            )}

            <SectionTitle>Item details</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  {...fieldProps("itemName")}
                  label={t("itemName")}
                  required
                  autoFocus
                  inputRef={nameRef}
                  helperText={fieldErrors.itemName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box data-field="unitName">
                  <UnitSelect
                    key={formKey}
                    label={`${t("unitName")} *`}
                    value={formData.unitName}
                    onChange={(v) => setField("unitName", v)}
                    size="medium"
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-notchedOutline": fieldErrors.unitName ? { borderColor: "error.main" } : {},
                      "& .MuiInputLabel-root": fieldErrors.unitName ? { color: "error.main" } : {},
                    }}
                  />
                  {fieldErrors.unitName && (
                    <Typography variant="caption" color="error" sx={{ ml: 1.75, mt: 0.5, display: "block" }}>
                      {fieldErrors.unitName}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...fieldProps("itemCode")}
                  label={t("itemCode")}
                  helperText={fieldErrors.itemCode || "Optional"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField {...fieldProps("barcode")} label={t("barcode")} required helperText={fieldErrors.barcode} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <SectionTitle>Pricing &amp; tax</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...fieldProps("standardPrice")}
                  label={t("standardPrice")}
                  required
                  type="number"
                  inputProps={{
                    min: "0",
                    step: priceDecimals ? "0.01" : keepsOldDecimalPrice ? "any" : "1",
                    inputMode: priceDecimals ? "decimal" : "numeric",
                  }}
                  helperText={fieldErrors.standardPrice || priceHelp}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...fieldProps("taxRate")}
                  label={t("taxRate")}
                  required
                  type="number"
                  inputProps={{ min: "0", step: "0.01", inputMode: "decimal" }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  helperText={fieldErrors.taxRate}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField {...fieldProps("hsnCode")} label={t("hsnCode")} required helperText={fieldErrors.hsnCode} />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button variant="outlined" color="inherit" onClick={resetForm} disabled={saving}>
                {editingItem ? "Cancel" : "Clear"}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : editingItem ? <Edit /> : <Add />}
                sx={{ minWidth: 160 }}
              >
                {editingItem ? t("updateItem") : "Save item"}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Item list */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
              sx={{ p: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("listOfItems")}
                </Typography>
                <Chip size="small" label={totalItems} />
              </Stack>
              <TextField
                size="small"
                placeholder="Search by name or code"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: { sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")} aria-label="Clear search">
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Stack>
            <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
            <TableContainer>
              <Table size="small" sx={{ "& th, & td": { px: { xs: 1, sm: 2 } } }}>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                    {sortable("itemName", t("itemName"))}
                    <TableCell sx={hideOnPhone}>{t("itemCode")}</TableCell>
                    <TableCell>{t("unitName")}</TableCell>
                    {sortable("standardPrice", t("standardPrice"), "right")}
                    <TableCell align="right" sx={hideOnPhone}>
                      {t("taxRate")}
                    </TableCell>
                    <TableCell align="right">{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      selected={editingItemId === item.id}
                      onClick={() => handleEdit(item)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.itemName}
                        </Typography>
                        {item.barcode && (
                          <Typography variant="caption" color="text.secondary">
                            {item.barcode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={hideOnPhone}>{item.itemCode}</TableCell>
                      <TableCell>{item.unitName}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatPrice(item.standardPrice)}
                      </TableCell>
                      <TableCell align="right" sx={hideOnPhone}>
                        {item.taxRate === null || item.taxRate === undefined || item.taxRate === "" ? "" : `${item.taxRate}%`}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title={t("editItem")}>
                          <IconButton
                            size="small"
                            aria-label={t("editItem")}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete item">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(item);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        {searchQuery ? `No items match "${searchQuery}".` : "No items yet. Add your first item using the form."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.itemName}</strong> will be removed from the item master. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
};

export default CreateItemMaster;
